import { useMemo } from "react";
import { createBrowserRouter, RouterProvider, RouteObject } from "react-router";
import { ROUTES } from "./routes";
import Home from "./components/Home";
import Invoices from "./components/Invoices";
import Appointments from "./components/Appointments";
import Forms from "./components/Forms";
import Documents from "./components/Documents";
import AppointmentBooking from "./components/AppointmentBooking";
import NotAvailable from "./components/ErrorPages/NotAvailable";
import WaitlistRequest from "./components/WaitlistRequest";
import ResetPassword from "./components/Account/ResetPassword";
import Telehealth from "./components/Telehealth/Telehealth";
import CustomForm from "./components/Forms/CustomForm/CustomForm";
import InvoicePay from "./components/InvoicePay";
import InvoiceView from "./components/InvoiceView";
import { ErrorPage } from "./components/ErrorPages/ErrorPage";
import StatementView from "./components/StatementView";
import { NotFound } from "./components/NotFound";
import { AccessDenied } from "./components/ErrorPages/AccessDenied";
import RouteGuard from "./components/RouteGuard";
import { FormSubmissionSuccess } from "./components/FormSubmissionSuccess";
import InvalidPortalUrl from "./components/ErrorPages/InvalidPortalUrl";
import SomethingWentWrong from "./components/ErrorPages/SomethingWentWrong";
import BrowserNotSupported from "./components/ErrorPages/BrowserNotSupported";
import LinkUnavailable from "./components/ErrorPages/LinkUnavailable";
import SubscriptionInvoices from "./components/SubscriptionInvoices";
import EmailPreferences from "./components/EmailPreferences";
import ClientPortal from "./ClientPortal";
import { BookingType } from "types/appointmentBooking";
import { LegacyFormRedirect, RedirectPreserveSearch } from "utils/legacyRedirects";

interface ClientPortalRouterProps {
    clientPortalGuid: string;
}

export default function ClientPortalRouter({ clientPortalGuid }: ClientPortalRouterProps) {
    const router = useMemo(() => {
        const routes: RouteObject[] = [
            {
                path: "clientportal/error",
                children: [
                    { index: true, element: <SomethingWentWrong /> },
                    { path: ROUTES.INVALID_PORTAL_URL.urlPath, element: <InvalidPortalUrl /> },
                    { path: ROUTES.BROWSER_NOT_SUPPORTED.urlPath, element: <BrowserNotSupported /> },
                    { path: ROUTES.LINK_UNAVAILABLE.urlPath, element: <LinkUnavailable /> },
                ],
            },
            {
                path: `clientportal/Subscription/${ROUTES.SUBSCRIPTION_INVOICES.urlPath}/:uniqueIdentifier`,
                element: <SubscriptionInvoices />,
            },
            {
                path: `clientportal/${ROUTES.EMAIL_PREFERENCES.urlPath}/:token`,
                element: <EmailPreferences />,
            },
        ];

        if (clientPortalGuid) {
            routes.push({
                path: `clientportal/${clientPortalGuid}`,
                element: <ClientPortal clientPortalGuid={clientPortalGuid} />,
                errorElement: <ErrorPage />,
                children: [
                    {
                        index: true,
                        element: (
                            <RedirectPreserveSearch
                                basePath={`/clientportal/${clientPortalGuid}`}
                                targetPath={ROUTES.HOME.urlPath}
                            />
                        ),
                    },
                    {
                        path: "forms",
                        element: (
                            <RedirectPreserveSearch
                                basePath={`/clientportal/${clientPortalGuid}`}
                                targetPath={ROUTES.FORMS.urlPath}
                            />
                        ),
                    },
                    {
                        path: "Invoices/ViewInvoice",
                        element: (
                            <RedirectPreserveSearch
                                basePath={`/clientportal/${clientPortalGuid}`}
                                targetPath={ROUTES.VIEW_INVOICE.urlPath}
                            />
                        ),
                    },
                    {
                        path: "Invoices/ViewStatement",
                        element: (
                            <RedirectPreserveSearch
                                basePath={`/clientportal/${clientPortalGuid}`}
                                targetPath={ROUTES.VIEW_STATEMENT.urlPath}
                            />
                        ),
                    },
                    {
                        path: "form/:publicId/:formDataNumber",
                        element: <LegacyFormRedirect basePath={`/clientportal/${clientPortalGuid}`} />,
                    },
                    {
                        path: ROUTES.HOME.urlPath,
                        element: <Home />,
                        children: [
                            {
                                path: "index",
                                element: <Home />,
                            },
                        ],
                    },
                    {
                        path: `${ROUTES.LOGIN.urlPath}/:clientNumber?`,
                        element: (
                            <RedirectPreserveSearch
                                basePath={`/clientportal/${clientPortalGuid}`}
                                targetPath={ROUTES.HOME.urlPath}
                            />
                        ),
                    },
                    {
                        path: ROUTES.INVOICES.urlPath,
                        element: (
                            <RouteGuard feature="canViewInvoicesList">
                                <Invoices />
                            </RouteGuard>
                        ),
                        children: [
                            {
                                path: "index",
                                element: <Invoices />,
                            },
                        ],
                    },
                    {
                        path: ROUTES.APPOINTMENTS.urlPath,
                        element: (
                            <RouteGuard feature="canViewUpcomingAppointments">
                                <Appointments />
                            </RouteGuard>
                        ),
                        children: [
                            {
                                path: "index",
                                element: <Appointments />,
                            },
                        ],
                    },
                    {
                        path: ROUTES.FORMS.urlPath,
                        element: (
                            <RouteGuard feature="canAccessFormsPage">
                                <Forms />
                            </RouteGuard>
                        ),
                        children: [
                            {
                                path: "index",
                                element: <Forms />,
                            },
                        ],
                    },
                    {
                        path: ROUTES.DOCUMENTS.urlPath,
                        element: (
                            <RouteGuard feature="canAccessDocuments">
                                <Documents />
                            </RouteGuard>
                        ),
                    },
                    {
                        path: ROUTES.APPOINTMENT_BOOKING.urlPath,
                        element: (
                            <RouteGuard feature="canBookAppointments">
                                <AppointmentBooking key="appointment-booking" bookingType={BookingType.CLIENT} />
                            </RouteGuard>
                        ),
                    },
                    {
                        path: `${ROUTES.APPOINTMENT_BOOKING.urlPath}/ClientAppointment`,
                        element: (
                            <RedirectPreserveSearch
                                basePath={`/clientportal/${clientPortalGuid}`}
                                targetPath={ROUTES.APPOINTMENT_BOOKING.urlPath}
                            />
                        ),
                    },
                    {
                        path: "AppointmentBooking/ClientAppointment",
                        element: (
                            <RedirectPreserveSearch
                                basePath={`/clientportal/${clientPortalGuid}`}
                                targetPath={ROUTES.APPOINTMENT_BOOKING.urlPath}
                            />
                        ),
                    },
                    {
                        path: "AppointmentBooking",
                        element: (
                            <RedirectPreserveSearch
                                basePath={`/clientportal/${clientPortalGuid}`}
                                targetPath={ROUTES.APPOINTMENT_BOOKING.urlPath}
                            />
                        ),
                    },
                    {
                        path: ROUTES.CLASS_BOOKING.urlPath,
                        element: (
                            <RouteGuard feature="canBookClasses">
                                <AppointmentBooking key="class-booking" bookingType={BookingType.CLASS} />
                            </RouteGuard>
                        ),
                    },
                    { path: ROUTES.WAITLIST_REQUEST.urlPath, element: <WaitlistRequest /> },
                    { path: `${ROUTES.TELEHEALTH.urlPath}/:id`, element: <Telehealth /> },
                    { path: `${ROUTES.TELEHEALTH_PLUS.urlPath}/:id`, element: <Telehealth /> },
                    {
                        path: `${ROUTES.REGISTRATION.urlPath}`,
                        element: <ResetPassword />,
                        children: [{ path: ":token", element: <ResetPassword /> }],
                    },
                    {
                        path: `${ROUTES.PUBLIC_FORM.urlPath}/:publicId/:formDataNumber`,
                        element: <CustomForm />,
                    },
                    { path: `${ROUTES.INVOICE_PAY.urlPath}/:clientId/:invoiceId`, element: <InvoicePay /> },
                    { path: ROUTES.ACCESS_DENIED.urlPath, element: <AccessDenied /> },
                    { path: ROUTES.FORM_SUBMISSION_SUCCESS.urlPath, element: <FormSubmissionSuccess /> },
                    {
                        path: `${ROUTES.VIEW_INVOICE.urlPath}`,
                        element: <InvoiceView />,
                    },
                    {
                        path: ROUTES.VIEW_STATEMENT.urlPath,
                        element: <StatementView />,
                    },
                    {
                        path: `${ROUTES.NOT_AVAILABLE.urlPath}`,
                        element: <NotAvailable />,
                    },
                    {
                        path: `error/${ROUTES.NOT_AVAILABLE.urlPath}`,
                        element: <NotAvailable />,
                    },
                    { path: "*", element: <NotFound /> },
                ],
            });
        }

        return createBrowserRouter(routes);
    }, [clientPortalGuid]);
    return <RouterProvider router={router} />;
}
